'use strict';

const libFableServiceProviderBase = require('fable-serviceproviderbase');

const libSampleScope = require('./Sample-Scope.js');
const libSampleTestRules = require('./SampleTest-Rules.js');

const MAX_CACHED_SCOPES = 25;

/**
 * Samples and the things connected to them. forSample() hands back a SampleScope that loads and keeps one sample's
 * connected records; SampleTestRules answers the same questions over records a caller already has.
 *
 * Reads go through `options.RestClient`, else the fable's `HeadlightRestClient` (headlight-rest-client), so the
 * provider works wherever that client is set up: a pict app in the browser or a Node tool.
 */
class HeadlightSamples extends libFableServiceProviderBase
{
	/**
	 * @param {Object} pFable
	 * @param {{ RestClient?: Object }} [pOptions]
	 * @param {String} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this.serviceType = 'HeadlightSamples';
		/** @type {Map<number, libSampleScope>} - IDSample -> its scope, oldest first */
		this._scopes = new Map();
	}

	/**
	 * @return {Object} The headlight-rest-client reads go through.
	 */
	get restClient()
	{
		return (this.options && this.options.RestClient) || this.fable.HeadlightRestClient;
	}

	/**
	 * The scope for a sample: the same one for every caller, so what one screen loaded the next one reuses. The
	 * MAX_CACHED_SCOPES most recently asked-for samples are kept.
	 *
	 * @param {number} pIDSample
	 * @return {libSampleScope}
	 */
	forSample(pIDSample)
	{
		const tmpIDSample = Number(pIDSample) || 0;
		let tmpScope = this._scopes.get(tmpIDSample);
		if (tmpScope)
		{
			this._scopes.delete(tmpIDSample);
		}
		else
		{
			tmpScope = new libSampleScope(this, tmpIDSample);
		}
		this._scopes.set(tmpIDSample, tmpScope);
		while (this._scopes.size > MAX_CACHED_SCOPES)
		{
			this._scopes.delete(this._scopes.keys().next().value);
		}
		return tmpScope;
	}

	/**
	 * The test summary row for one test instance (see SampleTest-Rules buildTestSummary), from the scope of the sample
	 * it belongs to: a scope that has it loaded, else its sample's, found through TestInstanceSampleJoin.
	 *
	 * @param {number} pIDTestInstance
	 * @return {Promise<?import('./SampleTest-Rules.js').TestSummaryRow>} null when the test instance is on no sample
	 */
	async getTestSummaryRow(pIDTestInstance)
	{
		const tmpIDTestInstance = Number(pIDTestInstance) || 0;
		if (!tmpIDTestInstance)
		{
			return null;
		}
		let tmpScope = [ ...this._scopes.values() ].reverse().find((pScope) => pScope.hasLoadedTestInstance(tmpIDTestInstance));
		if (!tmpScope)
		{
			const tmpJoins = await this.getEntitySet('TestInstanceSampleJoin', `FBV~IDTestInstance~EQ~${tmpIDTestInstance}`);
			const tmpJoin = tmpJoins.find((pJoin) => Number(pJoin.IDSample) > 0);
			if (!tmpJoin)
			{
				return null;
			}
			tmpScope = this.forSample(tmpJoin.IDSample);
		}
		const tmpRows = await tmpScope.getTestSummary();
		return tmpRows.find((pRow) => Number(pRow.TestInstance.IDTestInstance) === tmpIDTestInstance) || null;
	}

	/**
	 * A QuickSummaries record decorator for TestInstance (`HeadlightQuickSummaries.addRecordDecorator('TestInstance',
	 * ...)`): the result and remarks a linked form holds for approval, marked `(pending approval)`, and the linked
	 * documents' form statuses as `FormStatus`, so the popover says what a sample's Tests tab says.
	 *
	 * @param {Record<string, any>} pSummaryRecord - the summary record QuickSummaries built
	 * @param {Record<string, any>} pTestInstance - the TestInstance record
	 * @return {Promise<Record<string, any>>}
	 */
	async decorateTestInstanceSummary(pSummaryRecord, pTestInstance)
	{
		const tmpRow = await this.getTestSummaryRow(pTestInstance && pTestInstance.IDTestInstance);
		if (!tmpRow)
		{
			return pSummaryRecord;
		}
		if (tmpRow.ResultPending)
		{
			pSummaryRecord.TestResult = `${tmpRow.Result} (pending approval)`;
		}
		if (tmpRow.NotesPending)
		{
			pSummaryRecord.Notes = `${tmpRow.Notes} (pending approval)`;
		}
		const tmpStatuses = tmpRow.Documents.filter((pEntry) => pEntry.Status)
			.map((pEntry) => `${pEntry.Document.Name || `Document #${pEntry.Document.IDDocument}`}: ${pEntry.Status}`);
		pSummaryRecord.FormStatus = tmpStatuses.length ? tmpStatuses.join('; ') : 'N/A';
		return pSummaryRecord;
	}

	/**
	 * Every record of an entity matching a meadow filter, across pages.
	 *
	 * @param {String} pEntity
	 * @param {String} pFilter
	 * @return {Promise<Array<Object>>}
	 */
	getEntitySet(pEntity, pFilter)
	{
		return new Promise((fResolve, fReject) =>
		{
			const tmpClient = this.restClient;
			if (!tmpClient || (typeof tmpClient.getEntitySetWithPages !== 'function'))
			{
				return fReject(new Error('HeadlightSamples has no REST client; register HeadlightRestClient or pass options.RestClient.'));
			}
			tmpClient.getEntitySetWithPages(pEntity, pFilter, () =>
			{
				return undefined;
			}, () =>
			{
				return undefined;
			}, (pError, pRecords) =>
			{
				if (pError)
				{
					return fReject((pError instanceof Error) ? pError : new Error(`Reading ${pEntity} failed: ${pError}`));
				}
				return fResolve(Array.isArray(pRecords) ? pRecords : []);
			});
		});
	}
}

module.exports = HeadlightSamples;
module.exports.default_configuration = {};
module.exports.SampleScope = libSampleScope;
module.exports.SampleTestRules = libSampleTestRules;
